import { createBrowserHistory } from "history";
import { ROUTE_CONSTANTS_MAP } from "../routeConstants";
import type { PosScreen } from "./shared";

export * from "./shared";

/**
 * WEB variant — the browser-history singleton, verbatim from `apps/web/src/history.js`:
 *
 * ```js
 * import { createBrowserHistory } from 'history';
 * export default createBrowserHistory();
 * ```
 *
 * See `./shared.ts` for why navigation is owned by core rather than injected through the
 * effects bridge.
 *
 * ⚠️ It must stay a SINGLE instance: `CustomRouter.js` wraps react-router's `<Router>` around
 * this exact object, which is what lets code outside a component (an axios interceptor, a saga)
 * navigate. A second `createBrowserHistory()` would produce a router that listens to a
 * different history and silently ignores every `push`.
 *
 * `history` is a `peerDependency` of `@rista/core`, marked optional — mobile never resolves
 * this file, so Metro never requires the package.
 */
export const history = createBrowserHistory();

export default history;

/**
 * The route the browser is on right now.
 *
 * Read off the SAME `history` singleton the router is mounted on, not off `window.location` —
 * that is the object every out-of-component navigation goes through, so it can never disagree
 * with what the router rendered.
 */
export function getActiveRouteName(): string | undefined {
  return history.location?.pathname;
}

/** Is the ordering screen the one showing? See `./shared.ts` for the semantics. */
export function isNewOrderScreenActive(): boolean {
  return getActiveRouteName() === ROUTE_CONSTANTS_MAP.NEW_ORDER_ROUTE;
}

/**
 * The POS screen the feature gate is asked from — web `checkFeature`'s `currentTab`, verbatim:
 * the my-orders route, else the new-order route, else TABLE VIEW (any other path included).
 */
export function currentPosScreen(): PosScreen {
  const path = getActiveRouteName();
  return path?.includes(ROUTE_CONSTANTS_MAP.MY_ORDER_ROUTE)
    ? "my-orders"
    : path?.includes(ROUTE_CONSTANTS_MAP.NEW_ORDER_ROUTE)
    ? "new-order"
    : "table-view";
}
